<template>
  <div class="mail-template-categories-settings">
    <v-container fluid>
      <!-- Page Header -->
      <div class="d-flex justify-space-between align-center mb-6">
        <div>
          <h1 class="text-h4 font-weight-bold mb-2">فئات قوالب المراسلات</h1>
          <p class="text-body-1 text-grey">إدارة تصنيفات قوالب المراسلات في النظام</p>
        </div>
      </div>

      <!-- Content Card -->
      <v-card>
        <v-card-text class="pa-6">
          <div class="d-flex justify-space-between align-center mb-4">
            <h2 class="text-h5">قائمة فئات القوالب</h2>
            <v-btn
            v-if="hasPermission('admin.mail-template-categories.store')"
              v-action="{ module: 'mail-template-categories', action: 'store' }"
              color="primary"
              prepend-icon="mdi-plus"
              @click="openAddCategoryDialog"
            >
              إضافة فئة جديدة
            </v-btn>
          </div>

          <v-data-table
            :headers="categoryHeaders"
            :items="categories"
            :search="categorySearch"
            :loading="loading"
            color="primary"
            first-icon="mdi-chevron-left"
            last-icon="mdi-chevron-right"
            next-icon="mdi-chevron-right"
            prev-icon="mdi-chevron-left"
            show-current-page
            show-first-last-page
            show-first-page
            show-last-page
            show-next-page
            show-previous-page
            class="elevation-1"
          >
            <template #top>
              <v-text-field
                v-model="categorySearch"
                prepend-inner-icon="mdi-magnify"
                label="بحث في فئات القوالب"
                variant="outlined"
                density="compact"
                class="mx-4 mt-4"
              ></v-text-field>
            </template>


            <template v-slot:[`item.actions`]="{ item }">
              <v-btn
                v-if="hasPermission('admin.mail-template-categories.update')"
                v-action="{ module: 'mail-template-categories', action: 'update' }"
                icon
                size="small"
                color="primary"
                variant="text"
                @click="editCategory(item)"
              >
                <v-icon>mdi-pencil</v-icon>
              </v-btn>
              <v-btn
                v-if="hasPermission('admin.mail-template-categories.destroy')"
                v-action="{ module: 'mail-template-categories', action: 'destroy' }"
                icon
                size="small"
                color="error"
                variant="text"
                @click="confirmDelete(item, 'category')"
              >
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-container>

    <!-- Category Dialog -->
    <v-dialog v-model="showCategoryDialog" max-width="500px">
      <v-card>
        <v-card-title>
          {{ editingCategory ? 'تعديل فئة القوالب' : 'إضافة فئة قوالب جديدة' }}
        </v-card-title>
        <v-card-text>
          <v-form ref="categoryForm" v-model="valid" @submit.prevent="saveCategory">
            <v-text-field
              v-model="categoryForm.name"
              label="اسم الفئة"
              variant="outlined"
              required
              :rules="[v => !!v || 'اسم الفئة مطلوب']"
              class="mb-4"
              placeholder="قوالب إدارية، قوالب مالية..."
            ></v-text-field>
            
         
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showCategoryDialog = false">إلغاء</v-btn>
          <v-btn color="primary" @click="saveCategory">حفظ</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400px">
      <v-card>
        <v-card-title class="text-h6">تأكيد الحذف</v-card-title>
        <v-card-text>
          هل أنت متأكد من حذف فئة القوالب؟
          <br><strong>{{ itemToDelete?.name }}</strong>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="deleteDialog = false">إلغاء</v-btn>
          <v-btn color="error" @click="confirmDeleteAction">حذف</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import mailTemplateCategoryService from '@/services/mailTemplateCategoryService'
import { hasPermission, hasModule } from '@/utils/permissions'

export default {
  name: 'MailTemplateCategoriesView',
  data() {
    return {
      loading: false,
      valid: false,
      categorySearch: '',
      showCategoryDialog: false,
      editingCategory: null,
      categories: [],
      categoryForm: {
        name: '',
        desc: '',
        color: '#2196f3',
        active: true
      },
      categoryHeaders: [
        { title: 'اسم الفئة', key: 'name', align: 'start' },
        
        { title: 'الإجراءات', key: 'actions', align: 'center', sortable: false }
      ],
      deleteDialog: false,
      itemToDelete: null,
      deleteType: ''
    }
  },
  async mounted() {
    await this.loadCategories()
  },
  methods: {
    hasPermission(permission) {
      // Prevent permission checks during logout to avoid DOM errors
      if (this.$store.state.isLoggingOut) {
        return false
      }
      return hasPermission(permission)
    },
    
    hasModule(moduleName) {
      // Prevent permission checks during logout to avoid DOM errors
      if (this.$store.state.isLoggingOut) {
        return false
      }
      return hasModule(moduleName)
    },
    async loadCategories() {
      this.loading = true
      try {
        const response = await mailTemplateCategoryService.getMailTemplateCategories()
        if (response.data && response.data.data && Array.isArray(response.data.data)) {
          this.categories = response.data.data
        } else if (response.data && Array.isArray(response.data)) {
          this.categories = response.data
        } else {
          this.categories = []
        }
      } catch (error) {
        this.$store.dispatch('alerts/showAlert', {
          type: 'error',
          message: error.message || 'فشل في جلب فئات القوالب'
        })
      } finally {
        this.loading = false
      }
    },

    openAddCategoryDialog() {
      this.editingCategory = null
      this.categoryForm = {
        name: '',
        desc: '',
        color: '#2196f3',
        active: true
      }
      this.showCategoryDialog = true
    },

    editCategory(category) {
      this.editingCategory = category
      this.categoryForm = {
        name: category.name,
        desc: category.desc,
        color: category.color || '#2196f3',
        active: Boolean(category.active)
      }
      this.showCategoryDialog = true
    },

    async saveCategory() {
      if (!this.$refs.categoryForm.validate()) return

      this.loading = true
      try {
        if (this.editingCategory) {
          await mailTemplateCategoryService.updateMailTemplateCategory(this.editingCategory.id, this.categoryForm)
          this.$store.dispatch('alerts/showAlert', {
            type: 'success',
            message: 'تم تحديث فئة القوالب بنجاح'
          })
        } else {
          await mailTemplateCategoryService.createMailTemplateCategory(this.categoryForm)
          this.$store.dispatch('alerts/showAlert', {
            type: 'success',
            message: 'تم إضافة فئة القوالب بنجاح'
          })
        }
        
        this.showCategoryDialog = false
        await this.loadCategories()
      } catch (error) {
        this.$store.dispatch('alerts/showAlert', {
          type: 'error',
          message: error.message || 'فشل في حفظ فئة القوالب'
        })
      } finally {
        this.loading = false
      }
    },

    confirmDelete(item, type) {
      this.itemToDelete = item
      this.deleteType = type
      this.deleteDialog = true
    },

    async confirmDeleteAction() {
      if (!this.itemToDelete) return

      this.loading = true
      try {
        await mailTemplateCategoryService.deleteMailTemplateCategory(this.itemToDelete.id)
        this.$store.dispatch('alerts/showAlert', {
          type: 'success',
          message: 'تم حذف فئة القوالب بنجاح'
        })
        
        this.deleteDialog = false
        await this.loadCategories()
      } catch (error) {
        this.$store.dispatch('alerts/showAlert', {
          type: 'error',
          message: error.message || 'فشل في حذف فئة القوالب'
        })
      } finally {
        this.loading = false
      }
    },

    formatDate(date) {
      if (!date) return 'غير محدد'
      return new Date(date).toLocaleDateString('ar-LY', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    }
  }
}
</script>

<style scoped>
.mail-template-categories-settings {
  min-height: 100vh;
}
</style> 